import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import utc from 'dayjs/plugin/utc';
import { LocationZone } from '@gym-src/API';
import { Zone } from '@gym-particles/types/models';
import { getZonesForSiteLocation } from '@gym-graphql/queries';
import { removeZoneForSiteLocation } from '@gym-graphql/mutations';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const initialState: {
  isZonesLoading: boolean;
  items: Array<{
    siteLocationId: number;
    items: Zone[];
    totalRecords: number;
  }>;
} = {
  isZonesLoading: false,
  items: []
};

type paramType = {
  siteLocationId: number;
};
export type deleteParamType = {
  id?: number;
  userId: number;
};

export const fetchZones = createAsyncThunk('/gym/zones', async (params: paramType) => {
  const response = await (API.graphql({
    query: getZonesForSiteLocation,
    variables: {
      siteLocationId: params.siteLocationId
    }
  }) as Promise<{
    data: {
      getZonesForSiteLocation: {
        items: LocationZone[];
        totalRecords: { totalRecords: number };
      };
    };
  }>);
  return response;
});

export const deleteZone = createAsyncThunk('Zone/deleteZone', async (params: deleteParamType) => {
  const response = await (API.graphql({
    query: removeZoneForSiteLocation,
    variables: {
      id: params.id,
      userId: params.userId
    }
  }) as Promise<{
    data: {
      removeZoneForSiteLocation: number;
    };
  }>);
  return response;
});

export const ZonesSlice = createSlice({
  name: 'Zones',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchZones.fulfilled, (state, action) => {
      state.isZonesLoading = false;
      if (!action.payload.data.getZonesForSiteLocation.items) {
        return;
      }

      const items = action.payload.data.getZonesForSiteLocation.items.map(
        (zone: LocationZone): Zone => {
          return {
            id: zone.id || 0,
            siteLocationId: zone.siteLocationId || 0,
            zoneName: zone.zoneName || '',
            zoneDescription: zone.zoneDescription || '',
            arxReferenceId: zone.arxReferenceId || '',
            createdBy: zone.createdBy || 0,
            createdDate: zone.createdDate
              ? dayjs(zone.createdDate || '').format('DD MMM YYYY')
              : '',
            lastModifiedBy: zone.lastModifiedBy || 0,
            lastModifiedDate: zone.lastModifiedDate
              ? dayjs(zone.lastModifiedDate || '').format('DD MMM YYYY')
              : '',
            status: zone.status || '',
            displayStatus: zone.status
              ? zone.status.charAt(0).toUpperCase() + zone.status.slice(1)
              : '',
            accessCategoryId: zone.accessCategoryId || -1,
            accessCategoryName: zone.accessCategoryName || '',
            accessCategoryGender: zone.accessCategoryGender || '',
            accessCategoryAge: zone.accessCategoryAge ?? undefined,
            accessCategorySchedulesOnly: zone.accessCategorySchedulesOnly ?? undefined,
            accessCategoryStatus: zone.accessCategoryStatus || ''
          };
        }
      );
      const totalRecords = action.payload.data.getZonesForSiteLocation.totalRecords.totalRecords;

      const fetchedZonesInfo = {
        totalRecords: totalRecords,
        items: items,
        siteLocationId: action.meta.arg.siteLocationId
      };
      if (state.items.length === 0) {
        state.items.push(fetchedZonesInfo);
      } else {
        // Check if we are updating an already fetched or fetching new one
        const update = state.items.find(
          (ZonesInfo) => ZonesInfo.siteLocationId === fetchedZonesInfo.siteLocationId
        );
        if (update) {
          const newState = state.items.map((ZonesInfo) => {
            if (ZonesInfo.siteLocationId === fetchedZonesInfo.siteLocationId) {
              return fetchedZonesInfo;
            } else {
              return ZonesInfo;
            }
          });
          Object.assign(state.items, newState);
        } else {
          state.items.push(fetchedZonesInfo);
        }
      }
    });

    builder.addCase(fetchZones.pending, (state, action) => {
      state.isZonesLoading = true;
      return state;
    });

    builder.addCase(fetchZones.rejected, (state, action) => {
      state.isZonesLoading = false;
      console.log('Fetch Zones Error');
      // TO-Do: Add rejected flow here
      return state;
    });

    builder.addCase(deleteZone.fulfilled, (state, action) => {
      state.isZonesLoading = false;
      const zoneId = action.meta.arg.id;
      const filtered = state.items.map((zoneObj) => {
        const filtereZones = zoneObj.items.filter((zone) => zone.id !== zoneId);
        zoneObj.items = filtereZones;
        zoneObj.totalRecords = zoneObj.totalRecords - 1;
        return zoneObj;
      });
      state.items = filtered;
    });

    builder.addCase(deleteZone.pending, (state, action) => {
      state.isZonesLoading = true;
      return state;
    });

    builder.addCase(deleteZone.rejected, (state, action) => {
      state.isZonesLoading = false;
      console.error('deleteZones', action);
      return state;
    });
  }
});

export default ZonesSlice.reducer;
